<template>
  <div class="dashboard-container">
    <div class="page-header">
      <h2>筹码成本</h2>
      <p>核心股东成本与可收筹码推演。批次仅影响核心股东；在册浮筹为名册内未归核心的筹码，可按维度与心理价位筛选推演。</p>
    </div>

    <div class="filter-row">
      <el-form inline>
        <el-form-item label="数据更新时间">
          <el-select
            v-model="batch"
            placeholder="选择时间"
            style="width: 200px"
            :loading="loadingBatches"
            @change="onBatchChange"
          >
            <el-option
              v-for="b in batches"
              :key="b.value"
              :label="batchOptionLabel(b)"
              :value="b.value"
            />
          </el-select>
        </el-form-item>
        <el-form-item label="当前价">
          <span class="current-price">{{ kpi.current_price != null ? kpi.current_price.toFixed(2) : '—' }} 元/股</span>
        </el-form-item>
      </el-form>
    </div>

    <!-- 顶部 6 张 KPI：两大块（核心股东 / 浮筹），每块 3 张卡片 -->
    <div class="kpi-blocks">
      <!-- 第一部分：核心股东 -->
      <div class="kpi-block">
        <div class="kpi-block-title">核心股东</div>
        <div class="kpi-row">
          <div class="kpi-card">
            <div class="kpi-label">
              <span class="kpi-label-text">平均成本（FIFO）</span>
              <el-tooltip content="核心股东当前批次持仓按 FIFO 计算的加权平均成本（含全部核心账户），单位元/股。" placement="top">
                <span class="kpi-help">?</span>
              </el-tooltip>
            </div>
            <div class="kpi-value">{{ formatCost(kpi.core_avg_cost) }}<span class="unit">元/股</span></div>
            <div :class="['kpi-ring', (kpi.core_avg_cost_change_pct || 0) > 0 ? 'up' : (kpi.core_avg_cost_change_pct || 0) < 0 ? 'down' : 'flat']">
              较上期 {{ kpi.core_avg_cost_change_pct != null ? (kpi.core_avg_cost_change_pct >= 0 ? '+' : '') + kpi.core_avg_cost_change_pct : '—' }}%
            </div>
          </div>
          <div class="kpi-card">
            <div class="kpi-label">
              <span class="kpi-label-text">浮动盈亏（FIFO）</span>
              <el-tooltip content="核心股东当前批次持仓按 FIFO 成本与当前价之间的浮动盈亏，单位万元。" placement="top">
                <span class="kpi-help">?</span>
              </el-tooltip>
            </div>
            <div :class="['kpi-value', (kpi.core_float_pnl_wan || 0) >= 0 ? 'up' : 'down']">
              {{ kpi.core_float_pnl_wan != null ? (kpi.core_float_pnl_wan >= 0 ? '+' : '') + formatInt(kpi.core_float_pnl_wan) : '—' }}<span class="unit">万元</span>
            </div>
            <div :class="['kpi-ring', (kpi.core_float_pnl_change_pct || 0) > 0 ? 'up' : (kpi.core_float_pnl_change_pct || 0) < 0 ? 'down' : 'flat']">
              较上期 {{ kpi.core_float_pnl_change_pct != null ? (kpi.core_float_pnl_change_pct >= 0 ? '+' : '') + formatInt(kpi.core_float_pnl_change_pct) : '—' }}%
            </div>
          </div>
          <div class="kpi-card">
            <div class="kpi-label">
              <span class="kpi-label-text">现金盈亏（现金流）</span>
              <el-tooltip content="核心股东历史买卖的实际现金流盈亏（总取回金额 - 总投入），单位万元。" placement="top">
                <span class="kpi-help">?</span>
              </el-tooltip>
            </div>
            <div :class="['kpi-value', (kpi.core_cash_wan || 0) >= 0 ? 'up' : 'down']">
              {{ kpi.core_cash_wan != null ? (kpi.core_cash_wan >= 0 ? '+' : '') + formatInt(kpi.core_cash_wan) : '—' }}<span class="unit">万元</span>
            </div>
            <div :class="['kpi-ring', (kpi.core_cash_change_pct || 0) > 0 ? 'up' : (kpi.core_cash_change_pct || 0) < 0 ? 'down' : 'flat']">
              较上期 {{ kpi.core_cash_change_pct != null ? (kpi.core_cash_change_pct >= 0 ? '+' : '') + formatInt(kpi.core_cash_change_pct) : '—' }}%
            </div>
          </div>
        </div>
      </div>
      <!-- 第二部分：浮筹 -->
      <div class="kpi-block">
        <div class="kpi-block-title">浮筹</div>
        <div class="kpi-row">
          <div class="kpi-card">
            <div class="kpi-label">
              <span class="kpi-label-text">平均成本（FIFO）</span>
              <el-tooltip content="在册浮筹股东当前批次持仓按 FIFO 计算的加权平均成本，单位元/股。" placement="top">
                <span class="kpi-help">?</span>
              </el-tooltip>
            </div>
            <div class="kpi-value">{{ formatCost(kpi.retail_avg_cost) }}<span class="unit">元/股</span></div>
            <div :class="['kpi-ring', (kpi.retail_avg_cost_change_pct || 0) > 0 ? 'up' : (kpi.retail_avg_cost_change_pct || 0) < 0 ? 'down' : 'flat']">
              较上期 {{ kpi.retail_avg_cost_change_pct != null ? (kpi.retail_avg_cost_change_pct >= 0 ? '+' : '') + kpi.retail_avg_cost_change_pct : '—' }}%
            </div>
          </div>
          <div class="kpi-card">
            <div class="kpi-label">
              <span class="kpi-label-text">浮动盈亏（FIFO）</span>
              <el-tooltip content="在册浮筹按 FIFO 成本与当前价之间的浮动盈亏，单位万元。" placement="top">
                <span class="kpi-help">?</span>
              </el-tooltip>
            </div>
            <div :class="['kpi-value', (kpi.retail_float_pnl_wan || 0) >= 0 ? 'up' : 'down']">
              {{ kpi.retail_float_pnl_wan != null ? (kpi.retail_float_pnl_wan >= 0 ? '+' : '') + formatInt(kpi.retail_float_pnl_wan) : '—' }}<span class="unit">万元</span>
            </div>
            <div :class="['kpi-ring', (kpi.retail_float_pnl_change_pct || 0) > 0 ? 'up' : (kpi.retail_float_pnl_change_pct || 0) < 0 ? 'down' : 'flat']">
              较上期 {{ kpi.retail_float_pnl_change_pct != null ? (kpi.retail_float_pnl_change_pct >= 0 ? '+' : '') + formatInt(kpi.retail_float_pnl_change_pct) : '—' }}%
            </div>
          </div>
          <div class="kpi-card">
            <div class="kpi-label">
              <span class="kpi-label-text">现金盈亏（现金流）</span>
              <el-tooltip content="在册浮筹股东的历史现金流盈亏（总取回金额 - 总投入），单位万元。" placement="top">
                <span class="kpi-help">?</span>
              </el-tooltip>
            </div>
            <div :class="['kpi-value', (kpi.retail_cash_wan || 0) >= 0 ? 'up' : 'down']">
              {{ kpi.retail_cash_wan != null ? (kpi.retail_cash_wan >= 0 ? '+' : '') + formatInt(kpi.retail_cash_wan) : '—' }}<span class="unit">万元</span>
            </div>
            <div :class="['kpi-ring', (kpi.retail_cash_change_pct || 0) > 0 ? 'up' : (kpi.retail_cash_change_pct || 0) < 0 ? 'down' : 'flat']">
              较上期 {{ kpi.retail_cash_change_pct != null ? (kpi.retail_cash_change_pct >= 0 ? '+' : '') + formatInt(kpi.retail_cash_change_pct) : '—' }}%
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- 一、核心股东：与股权穿透一致，7 个一屏；超过 7 个或缩放 >110% 时减少展示数量 -->
    <section class="section chart-card" v-loading="loadingCore" element-loading-text="加载中...">
      <div class="card-title">
        <span>核心股东成本（{{ batchLabel }}）</span>
        <span class="card-hint">点击卡片查看成本驱动</span>
      </div>

      <div class="cards-navigation">
        <button class="nav-arrow left" @click="scrollCoreCards('left')">‹</button>
        <div
          :class="['shareholder-cards', 'core-cards-scroll', coreCardsFit ? 'core-cards-fit' : 'core-cards-reduced']"
          :data-count="coreCardsFit ? Math.min(7, Math.max(1, coreList.length)) : 4"
          ref="coreCardsRef"
        >
          <div
            v-for="(core, index) in coreList"
            :key="core.id"
            :class="['shareholder-card', selectedCoreId === core.id ? 'selected' : '']"
            @click="selectedCoreId = core.id"
          >
            <div class="card-header">
              <div class="shareholder-rank">{{ index + 1 }}</div>
              <div class="shareholder-info">
                <div class="shareholder-name">{{ core.name }}</div>
                <div class="shareholder-role">{{ core.controlledVolWan != null ? core.controlledVolWan : 0 }}万股</div>
              </div>
            </div>
            <div class="card-data card-data-single">
              <div class="data-main">
                <div class="data-value data-value--primary">{{ formatCost(core.avgCost) }}</div>
                <div class="data-unit">元/股</div>
              </div>
              <div :class="['data-change', core.avgCostChangePct > 0 ? 'positive' : core.avgCostChangePct < 0 ? 'negative' : '']" v-if="core.avgCostChangePct != null">
                {{ core.avgCostChangePct >= 0 ? '+' : '' }}{{ formatInt(core.avgCostChangePct) }}% 较上期
              </div>
              <div class="cost-trend-dots" v-if="core.recentCostTrend && core.recentCostTrend.length">
                <span
                  v-for="(point, idx) in core.recentCostTrend"
                  :key="idx"
                  :class="['trend-dot', point.change === 1 ? 'up' : point.change === -1 ? 'down' : 'flat']"
                  :title="`${point.label} ${formatCost(point.avgCost)} 元`"
                />
              </div>
            </div>
            <div class="control-types control-types-lines">
              <div class="control-line direct">
                <span class="control-label">账户控制 {{ (core.structure && core.structure.directCount) || 0 }}</span>
                <span class="control-cost">成本 {{ formatCost(core.structure && core.structure.directCost) }}</span>
              </div>
              <div class="control-line concert">
                <span class="control-label">一致行动 {{ (core.structure && core.structure.concertCount) || 0 }}</span>
                <span class="control-cost">成本 {{ formatCost(core.structure && core.structure.concertCost) }}</span>
              </div>
              <div class="control-line indirect">
                <span class="control-label">间接控制 {{ (core.structure && core.structure.indirectCount) || 0 }}</span>
                <span class="control-cost">成本 {{ formatCost(core.structure && core.structure.indirectCost) }}</span>
              </div>
            </div>
          </div>
        </div>
        <button class="nav-arrow right" @click="scrollCoreCards('right')">›</button>
      </div>

      <div v-if="selectedCoreData" class="core-callout">
        <div class="callout-title-row">
          <span class="callout-title">📊 {{ selectedCoreData.name }} — 成本驱动（按影响从高到低）</span>
        </div>
        <div class="callout-body drivers-body">
          <div class="drivers-block up">
            <div class="drivers-title">↑ 高于平均成本价的账户</div>
            <ul>
              <li v-for="(item, i) in costUpVisible" :key="'up-' + i">
                <a href="javascript:void(0)" class="holder-link" @click.stop="openHolderDetail(item.account, item.accountCode || item.account)">{{ item.account }}</a>：{{ item.desc }}
              </li>
              <li v-if="!selectedCoreData.costUp || !selectedCoreData.costUp.length" class="muted">—</li>
            </ul>
            <button v-if="selectedCoreData.costUp && selectedCoreData.costUp.length > defaultDriverCount" type="button" class="expand-btn" @click="expandedCostUp = !expandedCostUp">
              {{ expandedCostUp ? '收起' : `展开显示更多（共 ${selectedCoreData.costUp.length} 条）` }}
            </button>
          </div>
          <div class="drivers-block down">
            <div class="drivers-title">↓ 低于平均成本价的账户</div>
            <ul>
              <li v-for="(item, i) in costDownVisible" :key="'down-' + i">
                <a href="javascript:void(0)" class="holder-link" @click.stop="openHolderDetail(item.account, item.accountCode || item.account)">{{ item.account }}</a>：{{ item.desc }}
              </li>
              <li v-if="!selectedCoreData.costDown || !selectedCoreData.costDown.length" class="muted">—</li>
            </ul>
            <button v-if="selectedCoreData.costDown && selectedCoreData.costDown.length > defaultDriverCount" type="button" class="expand-btn" @click="expandedCostDown = !expandedCostDown">
              {{ expandedCostDown ? '收起' : `展开显示更多（共 ${selectedCoreData.costDown.length} 条）` }}
            </button>
          </div>
        </div>
      </div>
    </section>

    <!-- 二、可收筹码推演：筛选 + 在册浮筹名单 -->
    <section class="section chart-card">
      <div class="simulator-card retail-simulator">
        <div class="simulator-head">
          <span class="simulator-badge">推演</span>
          <span class="simulator-title">可收筹码推演</span>
          <span class="simulator-desc">按维度与心理价位筛选浮筹，下方为在册浮筹名单（无筛选时显示全部）</span>
        </div>
        <div class="simulator-body active">
          <div class="flow-top">
            <div class="flow-label">选择维度（点击或拖到下方）</div>
            <div class="flow-top-cards">
              <div class="dimension-glass price-zone-glass" :style="{ '--dim-color': '#a78bfa' }">
                <div class="dimension-name">价格范围</div>
                <div class="dimension-tags">
                  <button type="button" class="zone-btn" :class="{ active: selectedZone === 'low' }" @click="applyZone('low')">低价</button>
                  <button type="button" class="zone-btn" :class="{ active: selectedZone === 'mid' }" @click="applyZone('mid')">中价</button>
                  <button type="button" class="zone-btn" :class="{ active: selectedZone === 'high' }" @click="applyZone('high')">高价</button>
                </div>
              </div>
              <div v-for="grp in filterOptionGroups" :key="grp.dim" class="dimension-glass" :style="{ '--dim-color': grp.color }">
                <div class="dimension-name">{{ grp.label }}</div>
                <div class="dimension-tags">
                  <button v-for="opt in grp.options" :key="opt.id" type="button" class="dim-tag" draggable="true" @dragstart="onCriteriaDragStart($event, opt)" @click="addCriteria(opt)">
                    {{ opt.shortLabel }}
                  </button>
                </div>
              </div>
            </div>
            <div class="psych-range-row">
              <div class="psych-range-ctrl">
                <span class="psych-range-label">FIFO成本（元）</span>
                <span class="psych-range-value">{{ psychPriceRange[0].toFixed(2) }} ～ {{ psychPriceRange[1].toFixed(2) }}</span>
                <el-slider v-model="psychPriceRange" range :min="psychSliderMin" :max="psychSliderMax" :step="psychStep" :show-tooltip="true" class="psych-slider" />
              </div>
            </div>
          </div>
          <div class="flow-bottom">
            <div class="flow-label">当前推演条件</div>
            <div :class="['drop-zone', { 'drop-zone-active': dropZoneActive }]" @dragover.prevent="dropZoneActive = true" @dragleave="dropZoneActive = false" @drop="onCriteriaDrop">
              <template v-if="selectedCriteria.length || hasPsychRangeActive">
                <div class="selected-pills">
                  <span v-if="hasPsychRangeActive" class="pill pill-psych" @click="resetPsychRange">
                    {{ psychPriceRange[0].toFixed(2) }}～{{ psychPriceRange[1].toFixed(2) }}元
                    <span class="pill-close">×</span>
                  </span>
                  <span v-for="c in selectedCriteria" :key="c.id" class="pill" @click="removeCriteria(c.id)">{{ c.label }} <span class="pill-close">×</span></span>
                </div>
                <button type="button" class="clear-btn" @click="clearAllCriteria">清空条件</button>
              </template>
              <template v-else>
                <div class="drop-placeholder">
                  <span class="placeholder-icon">↓</span>
                  <span>将维度拖入此处或点击添加，上方滑块筛选价格</span>
                </div>
              </template>
            </div>
          </div>

          <div class="retail-result-glass">
            <div class="result-summary">
              <div class="summary-item">
                <span class="summary-value">{{ totalRetailAccountCount }}</span>
                <span class="summary-unit">户</span>
                <span class="summary-label">在册浮筹户数</span>
              </div>
              <div class="summary-divider" />
              <div class="summary-item">
                <span class="summary-value">{{ retailFiltered.length }}</span>
                <span class="summary-unit">户</span>
                <span class="summary-label">筛选后户数</span>
              </div>
              <div class="summary-divider" />
              <div class="summary-item">
                <span class="summary-value">{{ retailFilteredSummary.totalVol }}</span>
                <span class="summary-unit">万股</span>
                <span class="summary-label">合计持股</span>
              </div>
              <div class="summary-divider" />
              <div class="summary-item">
                <span class="summary-value">{{ retailFilteredSummary.estimatedFund }}</span>
                <span class="summary-unit">万元</span>
                <span class="summary-label">当前市值</span>
              </div>
              <div class="summary-divider" />
              <div class="summary-item">
                <span class="summary-value" :class="retailFilteredSummary.totalFloatPnl && retailFilteredSummary.totalFloatPnl.startsWith('+') ? 'up' : retailFilteredSummary.totalFloatPnl && retailFilteredSummary.totalFloatPnl.startsWith('-') ? 'down' : ''">{{ retailFilteredSummary.totalFloatPnl }}</span>
                <span class="summary-unit">万元</span>
                <span class="summary-label">浮动盈亏</span>
              </div>
              <template v-if="difficultyScore && (selectedCriteria.length || hasPsychRangeActive)">
                <div class="summary-divider" />
                <div class="summary-item">
                  <span class="summary-label">收购难度：</span>
                  <span class="diff-score" :class="'diff-' + (difficultyScore.score >= 7 ? 'hard' : difficultyScore.score >= 4 ? 'mid' : 'easy')">
                    {{ difficultyScore.score }}分（{{ difficultyScore.level }}）
                  </span>
                  <span class="diff-desc">{{ difficultyScore.desc }}</span>
                </div>
                <div class="summary-divider" />
                <div class="summary-item group-dist-item">
                  <div class="group-tags">
                    <span v-for="(count, group) in groupDistribution" :key="group" class="group-tag" :style="{ background: groupColorMap[group] + '22', color: groupColorMap[group], borderColor: groupColorMap[group] + '55' }">
                      {{ group }}:{{ count }}
                    </span>
                  </div>
                </div>
              </template>
            </div>
            <div class="result-table-wrap">
              <div class="result-table-header">名单明细（在册浮筹，与上方筛选联动）</div>
              <el-table :data="retailFilteredSorted" border stripe max-height="420" class="result-table retail-detail-table" v-loading="loadingRetail">
                <el-table-column type="index" label="序号" width="50" align="center" />
                <el-table-column label="股东名称" width="110" align="left" show-overflow-tooltip>
                  <template #default="{ row }">
                    <a href="javascript:void(0)" class="holder-link" @click.stop="openHolderDetail(row.holder_name, row.securities_account_no || row.account_code || row.raw_shareholder_id)">{{ row.holder_name }}</a>
                    <span v-if="row._isAssociate" class="assoc-inline" :title="row.holder_name + ' 仅关联'">🔗</span>
                  </template>
                </el-table-column>
                <el-table-column prop="hold_vol" label="持股(万股)" width="80" align="center" />
                <el-table-column label="当前市值(万元)" width="96" align="center">
                  <template #default="{ row }">{{ ((row.hold_vol || 0) * (kpi.current_price || 0)).toFixed(0) }}</template>
                </el-table-column>
                <el-table-column label="浮动盈亏(万元)" width="96" align="center">
                  <template #default="{ row }">
                    <span :class="((kpi.current_price - row.fifo_cost) * (row.hold_vol || 0)) > 0 ? 'cell-pnl profit' : ((kpi.current_price - row.fifo_cost) * (row.hold_vol || 0)) < 0 ? 'cell-pnl loss' : ''">
                      {{ ((kpi.current_price - row.fifo_cost) * (row.hold_vol || 0)).toFixed(0) }}
                    </span>
                  </template>
                </el-table-column>

                <el-table-column label="FIFO成本(元)" width="108" align="center">
                  <template #default="{ row }">{{ formatCost(row.fifo_cost) }}</template>
                </el-table-column>
                <el-table-column prop="trade_freq" label="交易频率" width="86" align="center">
                  <template #default="{ row }"><span :class="'cell-freq cell-freq-' + (row.trade_freq === '高' ? 'high' : row.trade_freq === '中' ? 'mid' : 'low')">{{ row.trade_freq }}</span></template>
                </el-table-column>
                <el-table-column prop="hold_scale" label="持股规模" width="86" align="center">
                  <template #default="{ row }"><span class="cell-scale">{{ row.hold_scale }}</span></template>
                </el-table-column>
                <el-table-column prop="pnl_status" label="盈亏状态" width="86" align="center">
                  <template #default="{ row }"><span :class="'cell-pnl ' + (row.pnl_status === '浮盈' ? 'profit' : row.pnl_status === '浮亏' ? 'loss' : '')">{{ row.pnl_status }}</span></template>
                </el-table-column>
                <el-table-column prop="cash_flow_status" label="现金流状态" width="96" align="center">
                  <template #default="{ row }"><span :class="'cell-cash ' + (row.cash_flow_status === '赚钱' ? 'profit' : row.cash_flow_status === '亏损' ? 'loss' : '')">{{ row.cash_flow_status }}</span></template>
                </el-table-column>
                <el-table-column prop="hold_stability" label="持仓稳定性" width="96" align="center">
                  <template #default="{ row }"><span class="cell-stab">{{ row.hold_stability }}</span></template>
                </el-table-column>
                <el-table-column label="群体类型" width="110" align="center">
                  <template #default="{ row }">
                    <span class="group-tag-cell" :style="{ color: groupColorMap[getHolderGroup(row)], borderColor: groupColorMap[getHolderGroup(row)] + '55' }">
                      {{ getHolderGroup(row) }}
                    </span>
                  </template>
                </el-table-column>
              </el-table>
            </div>
          </div>
      </div>
    </div>
    </section>

    <!-- 股东详情弹窗 -->
    <ShareholderDetailModal
      :key="(holderDetailName || '') + '|' + (holderDetailRawShareholderId || '')"
      v-model="holderDetailVisible"
      :holder-name="holderDetailName"
      :core-name="holderDetailCoreName"
      :link-type="holderDetailLinkType"
      :raw-shareholder-id="holderDetailRawShareholderId"
    />
  </div>
</template>

<script setup>
import { ref, reactive, computed, onMounted, onBeforeUnmount, watch } from 'vue'
import axios from 'axios'
import { ElMessage } from 'element-plus'
import ShareholderDetailModal from '@/components/ShareholderDetailModal.vue'

const defaultDriverCount = 5
const loadingBatches = ref(false)
const loadingKpi = ref(false)
const loadingCore = ref(false)
const loadingRetail = ref(false)

// 股东详情弹窗状态
const holderDetailVisible = ref(false)
const holderDetailName = ref('')
const holderDetailCoreName = ref('')
const holderDetailLinkType = ref(null)
const holderDetailRawShareholderId = ref('')

function openHolderDetail(name, rawShareholderId) {
  holderDetailName.value = name || ''
  holderDetailCoreName.value = ''
  holderDetailLinkType.value = null
  holderDetailRawShareholderId.value = rawShareholderId || ''
  holderDetailVisible.value = true
}

const batches = ref([])
const batch = ref('')
function batchOptionLabel(b) {
  if (!b) return ''
  if (b.date) return b.date
  const v = b.value || b.label || ''
  const m = v.match(/(\d{4})[-_]?(\d{2})[-_]?(\d{2})/)
  if (m) return `${m[1]}-${m[2]}-${m[3]}`
  return v
}
function formatCost(v) {
  if (v == null || v === '') return '—'
  const n = Number(v)
  if (Number.isNaN(n)) return '—'
  return n.toFixed(2)
}
/** 盈亏等金额/比例取整，不保留小数 */
function formatInt(v) {
  if (v == null || v === '') return '—'
  const n = Number(v)
  if (Number.isNaN(n)) return '—'
  return Math.round(n)
}
const batchLabel = computed(() => {
  const b = batches.value.find(x => x.value === batch.value)
  return b ? batchOptionLabel(b) : (batch.value || '—')
})

const kpi = reactive({
  core_avg_cost: null,
  core_avg_cost_change_pct: null,
  core_float_pnl_wan: null,
  core_float_pnl_change_pct: null,
  core_cash_wan: null,
  core_cash_change_pct: null,
  retail_avg_cost: null,
  retail_avg_cost_change_pct: null,
  retail_float_pnl_wan: null,
  retail_float_pnl_change_pct: null,
  retail_cash_wan: null,
  retail_cash_change_pct: null,
  current_price: null,
})

const coreList = ref([])
const coreCardsRef = ref(null)
const selectedCoreId = ref(null)

// 页面缩放（用于 >110% 时减少卡片展示数量）
const zoomScale = ref(1)
function updateZoom() {
  const vv = window.visualViewport
  if (vv && typeof vv.scale === 'number') {
    zoomScale.value = vv.scale
  } else {
    zoomScale.value = 1
  }
}
// 与股权穿透一致：7 个一屏；超过 7 个或缩放 >110% 时减少为 4 个一屏
const coreCardsFit = computed(() => {
  const n = coreList.value.length
  const z = zoomScale.value
  if (n > 7 || z > 1.1) return false
  return n > 0
})
const expandedCostUp = ref(false)
const expandedCostDown = ref(false)

const selectedCoreData = computed(() => coreList.value.find(c => c.id === selectedCoreId.value) || null)
const costUpVisible = computed(() => {
  if (!selectedCoreData.value || !selectedCoreData.value.costUp) return []
  const list = selectedCoreData.value.costUp
  return expandedCostUp.value ? list : list.slice(0, defaultDriverCount)
})
const costDownVisible = computed(() => {
  if (!selectedCoreData.value || !selectedCoreData.value.costDown) return []
  const list = selectedCoreData.value.costDown
  return expandedCostDown.value ? list : list.slice(0, defaultDriverCount)
})

const CORE_CARD_SCROLL_UNIT = 216
function scrollCoreCards(dir) {
  const el = coreCardsRef.value
  if (!el) return
  const amount = coreCardsFit.value ? el.clientWidth : (CORE_CARD_SCROLL_UNIT + 16) * 4
  el.scrollBy({ left: dir === 'left' ? -amount : amount, behavior: 'smooth' })
}

const retailList = ref([])
const associateList = ref([])
const associateTotalVolWan = ref(0)
const fifoBaseMin = ref(0)
const fifoBaseMax = ref(1)
const psychStep = 0.05
const psychPriceRange = ref([0, 1])
const selectedCriteria = ref([])
const draggedCriteria = ref(null)
const dropZoneActive = ref(false)

const totalRetailAccountCount = computed(() => {
  const retail = retailList.value.filter(r => (r.hold_vol || 0) > 0).length
  const associate = associateList.value.filter(a => (a.hold_vol || 0) > 0).length
  return retail + associate
})

const filterOptions = [
  { id: 'freq-high', dim: 'trade_freq', value: '高', label: '交易频率-高', shortLabel: '高' },
  { id: 'freq-mid', dim: 'trade_freq', value: '中', label: '交易频率-中', shortLabel: '中' },
  { id: 'freq-low', dim: 'trade_freq', value: '低', label: '交易频率-低', shortLabel: '低' },
  { id: 'scale-big', dim: 'hold_scale', value: '大', label: '持股规模-大', shortLabel: '大' },
  { id: 'scale-mid', dim: 'hold_scale', value: '中', label: '持股规模-中', shortLabel: '中' },
  { id: 'scale-small', dim: 'hold_scale', value: '小', label: '持股规模-小', shortLabel: '小' },
  { id: 'pnl-profit', dim: 'pnl_status', value: '浮盈', label: '盈亏状态-浮盈', shortLabel: '浮盈' },
  { id: 'pnl-flat', dim: 'pnl_status', value: '持平', label: '盈亏状态-持平', shortLabel: '持平' },
  { id: 'pnl-loss', dim: 'pnl_status', value: '浮亏', label: '盈亏状态-浮亏', shortLabel: '浮亏' },
  { id: 'cash-profit', dim: 'cash_flow_status', value: '赚钱', label: '现金流-已赚钱', shortLabel: '已赚钱' },
  { id: 'cash-flat', dim: 'cash_flow_status', value: '持平', label: '现金流-持平', shortLabel: '持平' },
  { id: 'cash-loss', dim: 'cash_flow_status', value: '亏损', label: '现金流-亏损', shortLabel: '亏损' },
  { id: 'stab-stable', dim: 'hold_stability', value: '稳定', label: '持仓稳定性-稳定', shortLabel: '稳定' },
  { id: 'stab-mid', dim: 'hold_stability', value: '一般', label: '持仓稳定性-一般', shortLabel: '一般' },
  { id: 'stab-active', dim: 'hold_stability', value: '活跃', label: '持仓稳定性-活跃', shortLabel: '活跃' },
]
const filterOptionGroups = [
  { dim: 'trade_freq', label: '交易频率', color: '#06b6d4', options: filterOptions.filter(o => o.dim === 'trade_freq') },
  { dim: 'hold_scale', label: '持股规模', color: '#f59e0b', options: filterOptions.filter(o => o.dim === 'hold_scale') },
  { dim: 'pnl_status', label: '盈亏状态', color: '#ec4899', options: filterOptions.filter(o => o.dim === 'pnl_status') },
  { dim: 'cash_flow_status', label: '现金流状态', color: '#8b5cf6', options: filterOptions.filter(o => o.dim === 'cash_flow_status') },
  { dim: 'hold_stability', label: '持仓稳定性', color: '#f97316', options: filterOptions.filter(o => o.dim === 'hold_stability') },
]

function addCriteria(opt) {
  const next = selectedCriteria.value.filter(c => c.dim !== opt.dim)
  next.push({ id: opt.id, dim: opt.dim, value: opt.value, label: opt.label })
  selectedCriteria.value = next
}

function onCriteriaDragStart(e, opt) {
  draggedCriteria.value = opt
  e.dataTransfer.effectAllowed = 'copy'
}

function onCriteriaDrop() {
  dropZoneActive.value = false
  if (!draggedCriteria.value) return
  addCriteria(draggedCriteria.value)
  draggedCriteria.value = null
}

function removeCriteria(id) {
  selectedCriteria.value = selectedCriteria.value.filter(c => c.id !== id)
}

function clearAllCriteria() {
  selectedCriteria.value = []
  resetPsychRange()
}
const selectedZone = ref(null)

const psychSliderMin = computed(() => fifoBaseMin.value)
const psychSliderMax = computed(() => fifoBaseMax.value)

function resetPsychRange() {
  psychPriceRange.value = [fifoBaseMin.value, fifoBaseMax.value]
  selectedZone.value = null
}

function applyZone(zone) {
  const minP = fifoBaseMin.value
  const maxP = fifoBaseMax.value
  const range = maxP - minP
  let recMin, recMax
  if (zone === 'low') {
    recMin = minP
    recMax = minP + range * 0.33
  } else if (zone === 'mid') {
    recMin = minP + range * 0.33
    recMax = minP + range * 0.67
  } else {
    recMin = minP + range * 0.67
    recMax = maxP
  }
  selectedZone.value = zone
  psychPriceRange.value = [parseFloat(recMin.toFixed(2)), parseFloat(recMax.toFixed(2))]
}

function getHolderGroup(row) {
  const { trade_freq, hold_scale, pnl_status, cash_flow_status, hold_stability } = row
  if (trade_freq === '高') return '活跃投机者'
  if (pnl_status === '浮亏' && hold_stability === '稳定' && hold_scale !== '小') return '回本等待型'
  if (cash_flow_status === '赚钱' && (trade_freq === '中' || trade_freq === '高')) return '现金流导向型'
  if (pnl_status === '浮盈' && hold_stability === '稳定') return '价值投资者'
  return '小规模灵活型'
}

const groupDistribution = computed(() => {
  const list = retailFiltered.value
  const dist = {}
  for (const row of list) {
    const g = getHolderGroup(row)
    dist[g] = (dist[g] || 0) + 1
  }
  return dist
})

const groupColorMap = {
  '活跃投机者': '#34d399',
  '价值投资者': '#60a5fa',
  '回本等待型': '#f87171',
  '现金流导向型': '#fbbf24',
  '小规模灵活型': '#94a3b8',
}

const difficultyScore = computed(() => {
  const list = retailFiltered.value
  if (!list.length) return null
  const dist = groupDistribution.value
  const cnt = list.length
  let score = 5
  const factors = []
  const activeSpeculator = dist['活跃投机者'] || 0
  const valueInvestor = dist['价值投资者'] || 0
  const backToEven = dist['回本等待型'] || 0
  const cashFlow = dist['现金流导向型'] || 0
  const flexible = dist['小规模灵活型'] || 0
  if (activeSpeculator / cnt > 0.4) { score += 2; factors.push('投机者多') }
  if (backToEven / cnt > 0.3) { score -= 2; factors.push('回本等待多') }
  if (valueInvestor / cnt > 0.4) { score -= 1; factors.push('价值投资者多') }
  if (cashFlow / cnt > 0.4) { score += 1; factors.push('现金流充裕') }
  if (flexible / cnt > 0.5) { score += 1; factors.push('小户灵活') }
  const bigHolders = list.filter(r => r.hold_scale === '大').length
  const stableHolders = list.filter(r => r.hold_stability === '稳定').length
  if (bigHolders / cnt > 0.4) { score -= 1; factors.push('大户集中') }
  if (stableHolders / cnt > 0.6) { score -= 1; factors.push('持仓稳定') }
  score = Math.max(1, Math.min(10, score))
  const level = score >= 7 ? '较难' : score >= 4 ? '中等' : '较易'
  const desc = factors.length ? `主要因为：${factors.join('、')}` : '各维度特征均衡'
  return { score, level, desc }
})

const hasPsychRangeActive = computed(() => {
  const [a, b] = psychPriceRange.value
  return a > fifoBaseMin.value || b < fifoBaseMax.value
})

const retailFiltered = computed(() => {
  const [psychMin, psychMax] = psychPriceRange.value
  const retailRows = retailList.value
    .filter(row => {
      if ((row.hold_vol || 0) <= 0) return false
      const price = row.fifo_cost > 0 ? row.fifo_cost : null
      if (price != null && (price < psychMin || price > psychMax)) return false
      if (!selectedCriteria.value.length) return true
      return selectedCriteria.value.every(c => {
        if (c.dim === 'trade_freq') return row.trade_freq === c.value
        if (c.dim === 'hold_scale') return row.hold_scale === c.value
        if (c.dim === 'pnl_status') return row.pnl_status === c.value
        if (c.dim === 'cash_flow_status') return row.cash_flow_status === c.value
        if (c.dim === 'hold_stability') return row.hold_stability === c.value
        return false
      })
    })
    .map(row => ({ ...row, _isAssociate: false }))

  const associateRows = associateList.value
    .filter(a => {
      if ((a.hold_vol || 0) <= 0) return false
      const price = a.fifo_cost > 0 ? a.fifo_cost : null
      if (price != null && (price < psychMin || price > psychMax)) return false
      return true
    })
    .map(a => ({ ...a, _isAssociate: true }))

  return [...retailRows, ...associateRows]
})


const retailFilteredSorted = computed(() => {
  return [...retailFiltered.value].sort((a, b) => (b.hold_vol ?? 0) - (a.hold_vol ?? 0))
})

const retailFilteredSummary = computed(() => {
  const list = retailFiltered.value
  const totalVol = list.reduce((s, r) => s + (r.hold_vol || 0), 0)
  const currentPrice = kpi.current_price || 0
  const marketValue = currentPrice * totalVol
  const totalFloatPnl = list.reduce((s, r) => {
    const price = r.fifo_cost || 0
    return s + (currentPrice - price) * (r.hold_vol || 0)
  }, 0)
  return {
    totalVol: totalVol.toFixed(1),
    estimatedFund: currentPrice > 0 ? String(Math.round(marketValue)) : '—',
    totalFloatPnl: currentPrice > 0 ? (totalFloatPnl >= 0 ? '+' : '') + Math.round(totalFloatPnl) : '—',
  }
})

async function fetchBatches() {
  loadingBatches.value = true
  try {
    const res = await axios.get('/api/chip_cost/batches/')
    if (res.data && res.data.status === 'ok' && res.data.data) {
      batches.value = res.data.data
      if (batches.value.length && !batch.value) batch.value = batches.value[0].value
    }
  } finally {
    loadingBatches.value = false
  }
}

async function fetchKpi() {
  if (!batch.value) return
  loadingKpi.value = true
  try {
    const res = await axios.get('/api/chip_cost/kpi/', { params: { batch: batch.value } })
    if (res.data && res.data.status === 'ok' && res.data.data) {
      Object.assign(kpi, res.data.data)
    }
  } finally {
    loadingKpi.value = false
  }
}

async function fetchCoreList() {
  if (!batch.value) return
  loadingCore.value = true
  try {
    const res = await axios.get('/api/chip_cost/core_list/', { params: { batch: batch.value } })
    if (res.data && res.data.status === 'ok' && res.data.data) {
      coreList.value = res.data.data.filter(
        c =>
          (c.controlledVolWan ?? 0) > 0 ||
          (c.totalVol ?? 0) > 0 ||
          (c.avgCost ?? 0) > 0
      )
      if (coreList.value.length && !selectedCoreId.value) selectedCoreId.value = coreList.value[0].id
    }
  } finally {
    loadingCore.value = false
  }
}

async function fetchRetailList() {
  loadingRetail.value = true
  try {
    const res = await axios.get('/api/chip_cost/retail_list/', {
      params: batch.value ? { batch: batch.value } : {},
    })
    if (res.data && res.data.status === 'ok' && res.data.data) {
      retailList.value = res.data.data.list || []
      const min = res.data.data.psych_range_min
      const max = res.data.data.psych_range_max
      fifoBaseMin.value = typeof min === 'number' ? min : 0
      fifoBaseMax.value = typeof max === 'number' && max > fifoBaseMin.value ? max : fifoBaseMin.value + 1
      psychPriceRange.value = [fifoBaseMin.value, fifoBaseMax.value]
    }
  } finally {
    loadingRetail.value = false
  }
}

async function fetchAssociateList() {
  try {
    const res = await axios.get('/api/chip_cost/associate_list/')
    if (res.data?.status === 'ok' && res.data?.data) {
      associateList.value = res.data.data.list || []
      associateTotalVolWan.value = res.data.data.total_vol_wan || 0
      const allCosts = [
        ...retailList.value.map(r => r.fifo_cost).filter(v => v > 0),
        ...associateList.value.map(a => a.fifo_cost).filter(v => v > 0),
      ]
      if (allCosts.length) {
        const minC = Math.min(...allCosts)
        const maxC = Math.max(...allCosts)
        fifoBaseMin.value = minC
        fifoBaseMax.value = maxC
        psychPriceRange.value = [minC, maxC]
      }
    }
  } catch (e) {
    console.error('fetchAssociateList error:', e)
  }
}

function onBatchChange() {
  fetchKpi()
  fetchCoreList()
  fetchRetailList()
}

onMounted(async () => {
  updateZoom()
  if (window.visualViewport) {
    window.visualViewport.addEventListener('resize', updateZoom)
    window.visualViewport.addEventListener('scroll', updateZoom)
  }
  window.addEventListener('resize', updateZoom)
  await fetchBatches()
  await Promise.all([fetchKpi(), fetchCoreList(), fetchRetailList()])
  fetchAssociateList()
  if (coreList.value.length) selectedCoreId.value = coreList.value[0].id
})
onBeforeUnmount(() => {
  if (window.visualViewport) {
    window.visualViewport.removeEventListener('resize', updateZoom)
    window.visualViewport.removeEventListener('scroll', updateZoom)
  }
  window.removeEventListener('resize', updateZoom)
})
</script>

<style scoped>
.holder-link {
  color: #409eff;
  text-decoration: none;
  cursor: pointer;
}
.holder-link:hover {
  text-decoration: underline;
}
.dashboard-container {
  padding: 20px;
  background: #f5f7fa;
  min-height: 100vh;
  width: 100%;
  max-width: 100%;
  box-sizing: border-box;
}
.page-header { margin-bottom: 20px; }
.page-header h2 { margin: 0 0 6px 0; font-size: 24px; font-weight: 700; color: #0f172a; }
.page-header p { margin: 0; font-size: 14px; color: #64748b; }
.filter-row { margin-bottom: 20px; }
.current-price { font-weight: 600; color: #0d9488; }

.kpi-blocks { display: flex; flex-direction: row; gap: 20px; flex-wrap: nowrap; }
.kpi-block {
  flex: 1;
  min-width: 0;
  background: #f8fafc;
  border-radius: 12px;
  padding: 16px 20px;
  border: 1px solid #e2e8f0;
}
.kpi-block-title {
  font-size: 16px;
  font-weight: 600;
  color: #0f172a;
  margin-bottom: 12px;
  padding-bottom: 8px;
  border-bottom: 2px solid #2563eb;
  display: inline-block;
}
.kpi-row { display: grid; grid-template-columns: repeat(3, 1fr); gap: 12px; margin-bottom: 0; }
.kpi-block .kpi-row { margin-bottom: 0; }
.kpi-card { background: #fff; border-radius: 12px; padding: 16px 18px; box-shadow: 0 1px 3px rgba(0,0,0,0.06); position: relative; }
.kpi-label { font-size: 14px; color: #64748b; font-weight: 500; margin-bottom: 10px; display: flex; align-items: center; justify-content: space-between; gap: 6px; }
.kpi-label-text { flex: 1; }
.kpi-help { width: 18px; height: 18px; border-radius: 50%; border: 1px solid #94a3b8; color: #0f172a; font-size: 12px; font-weight: 700; display: inline-flex; align-items: center; justify-content: center; cursor: pointer; background: #e0f2fe; }
.kpi-value { font-size: 26px; font-weight: 700; color: #0f172a; }
.kpi-value .unit { font-size: 14px; font-weight: 500; color: #64748b; margin-left: 2px; }
.kpi-value.up { color: #dc2626; }
.kpi-value.down { color: #10b981; }
.kpi-ring { font-size: 12px; margin-top: 6px; color: #94a3b8; }
.kpi-ring.up { color: #dc2626; }
.kpi-ring.down { color: #10b981; }
.kpi-ring.flat { color: #94a3b8; }

.section { margin-bottom: 20px; min-width: 0; }
.chart-card {
  background: #fff;
  border-radius: 12px;
  padding: 20px;
  box-shadow: 0 1px 3px rgba(0,0,0,0.06);
  overflow: hidden;
  min-width: 0;
}
.card-title {
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 8px;
  font-size: 18px;
  font-weight: 600;
  color: #0f172a;
  margin-bottom: 16px;
}
.card-hint { font-size: 13px; color: #94a3b8; font-weight: 400; }

.cards-navigation { display: flex; align-items: center; gap: 12px; margin-bottom: 16px; min-width: 0; }
.nav-arrow { width: 36px; height: 36px; border: 1px solid #e2e8f0; border-radius: 8px; background: #fff; cursor: pointer; font-size: 20px; color: #64748b; flex-shrink: 0; }
.nav-arrow:hover { border-color: #2563eb; color: #2563eb; }
/* 核心股东卡片：与股权穿透一致，7 个一屏；超过 7 个或缩放 >110% 时 4 个一屏 */
.core-cards-scroll { display: flex; gap: 16px; overflow-x: auto; padding-bottom: 12px; scroll-behavior: smooth; flex: 1; min-width: 0; cursor: grab; user-select: none; }
.core-cards-scroll::-webkit-scrollbar { height: 6px; }
.core-cards-scroll::-webkit-scrollbar-track { background: #f1f1f1; border-radius: 3px; }
.core-cards-scroll::-webkit-scrollbar-thumb { background: #c1c1c1; border-radius: 3px; }
/* 7 个及以内且缩放≤110%：网格一屏排布，与股权穿透一致 */
.core-cards-scroll.core-cards-fit {
  display: grid;
  overflow-x: visible;
  cursor: default;
}
.core-cards-scroll.core-cards-fit[data-count="1"] { grid-template-columns: repeat(1, minmax(0, 1fr)); }
.core-cards-scroll.core-cards-fit[data-count="2"] { grid-template-columns: repeat(2, minmax(0, 1fr)); }
.core-cards-scroll.core-cards-fit[data-count="3"] { grid-template-columns: repeat(3, minmax(0, 1fr)); }
.core-cards-scroll.core-cards-fit[data-count="4"] { grid-template-columns: repeat(4, minmax(0, 1fr)); }
.core-cards-scroll.core-cards-fit[data-count="5"] { grid-template-columns: repeat(5, minmax(0, 1fr)); }
.core-cards-scroll.core-cards-fit[data-count="6"] { grid-template-columns: repeat(6, minmax(0, 1fr)); }
.core-cards-scroll.core-cards-fit[data-count="7"] { grid-template-columns: repeat(7, minmax(0, 1fr)); }
.core-cards-scroll.core-cards-fit .shareholder-card { width: 100%; min-width: 0; max-width: none; flex: none; }
/* 超过 7 个或缩放>110%：固定宽度约 4 个一屏 */
.core-cards-scroll.core-cards-reduced .shareholder-card {
  flex: 0 0 auto;
  width: 232px;
  min-width: 232px;
  max-width: 320px;
}
.shareholder-cards { display: flex; gap: 16px; overflow-x: auto; padding-bottom: 12px; scroll-behavior: smooth; flex: 1; min-width: 0; }
.shareholder-card {
  flex: 0 0 auto;
  width: 200px;
  min-width: 200px;
  max-width: 320px;
  background: #f8fafc;
  border: 2px solid #e2e8f0;
  border-radius: 12px;
  padding: 16px;
  cursor: pointer;
  transition: all 0.2s;
}
.shareholder-card:hover { border-color: #2563eb; }
.shareholder-card.selected { border-color: #2563eb; background: #eff6ff; }
.shareholder-rank { background: #0f172a; }
.data-percent { color: #2563eb; }
.control-item.full-control .bar-fill { background: #2563eb; }
.control-item.indirect-control .bar-fill { background: #f59e0b; }
.card-header { display: flex; align-items: center; margin-bottom: 12px; }
.shareholder-rank { width: 32px; height: 32px; color: #fff; border-radius: 50%; display: flex; align-items: center; justify-content: center; font-size: 14px; font-weight: 600; margin-right: 12px; }
.shareholder-info { flex: 1; }
.shareholder-name { font-size: 18px; font-weight: 600; color: #0f172a; }
.shareholder-role { font-size: 13px; color: #64748b; }
.card-data { display: flex; align-items: baseline; justify-content: space-between; margin-bottom: 12px; padding-bottom: 12px; border-bottom: 1px solid #e2e8f0; }
.data-main { display: flex; align-items: baseline; }
.data-value { font-size: 32px; font-weight: 700; color: #0f172a; }
.data-unit { font-size: 14px; color: #64748b; margin-left: 4px; }
.data-percent { font-size: 20px; font-weight: 600; }
.control-types { margin-bottom: 0; }
.control-types-lines { display: flex; flex-direction: column; gap: 6px; }
.control-types-lines .control-line { display: flex; justify-content: space-between; align-items: center; font-size: 12px; }
.control-types-lines .control-label { width: auto; color: #64748b; }
.control-types-lines .control-cost { font-weight: 600; color: #0f172a; }
.control-item { display: flex; align-items: center; margin-bottom: 8px; }
.control-label { width: 70px; font-size: 12px; color: #64748b; }
.control-bar { flex: 1; height: 6px; background: #e2e8f0; border-radius: 3px; margin: 0 8px; overflow: hidden; }
.control-value { width: 40px; font-size: 12px; font-weight: 600; color: #0f172a; text-align: right; }
.card-data-single { margin-bottom: 10px; display: flex; flex-wrap: wrap; align-items: baseline; gap: 8px; }
.card-data-single .data-value { font-size: 24px; }
.card-data-single .data-value--primary { color: #2563EB; }
.card-data-single .data-main .data-unit { color: #2563EB; }
.card-data-single .data-change { font-size: 12px; color: #64748b; }
.card-data-single .data-change.positive { color: #dc2626; }
.card-data-single .data-change.negative { color: #10b981; }
.cost-trend-dots { width: 100%; display: flex; align-items: center; gap: 4px; margin-top: 6px; }
.cost-trend-dots .trend-dot { width: 8px; height: 8px; border-radius: 50%; flex-shrink: 0; }
.cost-trend-dots .trend-dot.up { background: #dc2626; }
.cost-trend-dots .trend-dot.down { background: #10b981; }
.cost-trend-dots .trend-dot.flat { background: #94a3b8; }

.core-callout { background: #fff; border-radius: 12px; padding: 16px 20px; box-shadow: 0 1px 4px rgba(0,0,0,0.06); }
.callout-title-row { margin-bottom: 12px; }
.callout-title { font-size: 15px; font-weight: 600; color: #303133; }
.drivers-body { display: grid; grid-template-columns: 1fr 1fr; gap: 16px; }
.drivers-block { background: #fafafa; border-radius: 8px; padding: 14px; font-size: 13px; }
.drivers-block.up { border-left: 4px solid #dc2626; }
.drivers-block.down { border-left: 4px solid #10b981; }
.drivers-title { font-weight: 600; margin-bottom: 10px; color: #303133; }
.drivers-block ul { margin: 0 0 10px 0; padding-left: 18px; }
.drivers-block li { margin: 4px 0; }
.drivers-block .muted { color: #909399; list-style: none; padding-left: 0; }
.expand-btn { font-size: 12px; color: #2563eb; background: none; border: none; cursor: pointer; padding: 0; }
.expand-btn:hover { text-decoration: underline; }

.retail-simulator { background: linear-gradient(145deg, #0c1222 0%, #1a1f35 40%, #162032 100%); color: #fff; border-radius: 16px; padding: 24px; box-shadow: 0 8px 32px rgba(0,0,0,0.2), 0 0 0 1px rgba(255,255,255,0.04); }
.retail-simulator .simulator-head { display: flex; align-items: center; gap: 12px; margin-bottom: 20px; flex-wrap: wrap; }
.simulator-badge { font-size: 11px; font-weight: 600; text-transform: uppercase; letter-spacing: 0.06em; color: #38bdf8; background: rgba(56,189,248,0.15); padding: 4px 10px; border-radius: 6px; }
.simulator-title { font-size: 18px; font-weight: 600; color: #f1f5f9; }
.retail-simulator .simulator-desc { font-size: 13px; color: #94a3b8; font-weight: 400; }
.flow-label { font-size: 11px; text-transform: uppercase; letter-spacing: 0.05em; color: #64748b; margin-bottom: 10px; }
.flow-top { margin-bottom: 20px; }
.flow-top-cards { display: flex; flex-wrap: wrap; gap: 12px; margin-bottom: 16px; }
.dimension-glass { background: rgba(255,255,255,0.06); backdrop-filter: blur(12px); border-radius: 12px; padding: 12px 14px; border: 1px solid rgba(255,255,255,0.08); border-left: 3px solid var(--dim-color, #64748b); transition: transform 0.15s, box-shadow 0.15s; }
.dimension-glass:hover { background: rgba(255,255,255,0.08); box-shadow: 0 4px 16px rgba(0,0,0,0.15); }
.dimension-name { font-size: 12px; color: #94a3b8; margin-bottom: 8px; font-weight: 500; }
.dimension-tags { display: flex; flex-wrap: wrap; gap: 6px; }
.dim-tag { background: rgba(255,255,255,0.12); color: #e2e8f0; border: 1px solid rgba(255,255,255,0.12); padding: 5px 10px; border-radius: 6px; font-size: 12px; cursor: grab; transition: all 0.15s; }
.dim-tag:hover { background: var(--dim-color); color: #fff; border-color: var(--dim-color); transform: translateY(-1px); }
.price-zone-glass .zone-btn { background: rgba(167,139,250,0.15); border: 1px solid rgba(167,139,250,0.3); color: #c4b5fd; padding: 5px 14px; border-radius: 6px; font-size: 12px; cursor: pointer; transition: all 0.15s; font-weight: 500; }
.price-zone-glass .zone-btn:hover { background: rgba(139,92,246,0.3); border-color: #8b5cf6; color: #e9d5ff; }
.price-zone-glass .zone-btn.active { background: rgba(139,92,246,0.4); border-color: #a855f7; color: #f0e8ff; font-weight: 600; }
.pill-zone { background: rgba(139,92,246,0.25); color: #c084fc; border-color: rgba(139,92,246,0.4); }
.flow-bottom { margin-bottom: 20px; }
.pill { display: inline-flex; align-items: center; gap: 6px; background: rgba(56,189,248,0.2); color: #7dd3fc; padding: 6px 12px; border-radius: 8px; font-size: 13px; cursor: pointer; border: 1px solid rgba(56,189,248,0.3); transition: background 0.15s; }
.pill:hover { background: rgba(56,189,248,0.3); }
.pill-close { opacity: 0.8; font-size: 14px; }
.pill-adj { background: rgba(139,92,246,0.3); color: #c084fc; border-radius: 4px; padding: 0 5px; font-size: 12px; font-weight: 600; border: 1px solid rgba(139,92,246,0.4); }
.pill-psych { background: rgba(139,92,246,0.2); color: #c084fc; border-color: rgba(139,92,246,0.4); }
.psych-range-row { display: flex; align-items: center; padding: 12px 14px; background: rgba(139, 92, 246, 0.1); border-radius: 10px; border-left: 3px solid #8b5cf6; margin-top: 12px; gap: 16px; }
.psych-range-ctrl { display: flex; align-items: center; gap: 12px; flex: 1; }
.psych-range-label { font-size: 13px; color: #a78bfa; font-weight: 500; white-space: nowrap; }
.psych-range-value { font-size: 12px; color: #c4b5fd; white-space: nowrap; min-width: 90px; }
.retail-simulator .psych-slider { flex: 1; }
.retail-simulator .psych-slider :deep(.el-slider__runway) { background-color: rgba(255,255,255,0.15); }
.retail-simulator .psych-slider :deep(.el-slider__bar) { background: linear-gradient(90deg, #8b5cf6, #a78bfa); }
.retail-simulator .psych-slider :deep(.el-slider__button) { border-color: #a78bfa; background: #8b5cf6; }
.difficulty-row { display: flex; align-items: center; gap: 12px; padding: 10px 14px; background: rgba(255,255,255,0.05); border-radius: 8px; margin-top: 10px; flex-wrap: wrap; }
.diff-label { font-size: 12px; color: #94a3b8; font-weight: 500; }
.diff-score { font-size: 15px; font-weight: 700; }
.diff-score.diff-hard { color: #f87171; }
.diff-score.diff-mid { color: #fbbf24; }
.diff-score.diff-easy { color: #34d399; }
.diff-desc { font-size: 12px; color: #64748b; }
.drop-zone { min-height: 88px; padding: 16px 20px; border-radius: 12px; background: rgba(255,255,255,0.04); border: 2px dashed rgba(255,255,255,0.15); display: flex; flex-wrap: wrap; align-items: center; gap: 10px; transition: all 0.2s; }
.drop-zone.drop-zone-active { background: rgba(56,189,248,0.12); border-color: #38bdf8; box-shadow: 0 0 0 3px rgba(56,189,248,0.2); }
.selected-pills { display: flex; flex-wrap: wrap; gap: 8px; align-items: center; }
.clear-btn { background: none; border: 1px solid rgba(255,255,255,0.2); color: #94a3b8; padding: 5px 12px; border-radius: 6px; font-size: 12px; cursor: pointer; }
.clear-btn:hover { border-color: #64748b; color: #cbd5e1; }
.drop-placeholder { display: flex; flex-direction: column; align-items: center; justify-content: center; gap: 8px; color: #64748b; font-size: 13px; width: 100%; min-height: 48px; }
.placeholder-icon { font-size: 24px; opacity: 0.6; }
.retail-result-glass { background: rgba(255,255,255,0.04); backdrop-filter: blur(10px); border-radius: 12px; padding: 16px; border: 1px solid rgba(255,255,255,0.06); }
.result-summary { display: flex; align-items: center; gap: 24px; margin-bottom: 16px; padding-bottom: 14px; border-bottom: 1px solid rgba(255,255,255,0.06); }
.summary-item { display: flex; align-items: baseline; gap: 6px; flex-wrap: wrap; }
.summary-value { font-size: 22px; font-weight: 700; color: #f1f5f9; }
.summary-value.up { color: #dc2626; }
.summary-value.down { color: #10b981; }
.summary-unit { font-size: 13px; color: #94a3b8; }
.summary-label { font-size: 12px; color: #64748b; width: 100%; margin-top: 2px; }
.summary-divider { width: 1px; height: 28px; background: rgba(255,255,255,0.1); }
.result-table-header { font-size: 12px; color: #94a3b8; margin-bottom: 10px; text-transform: uppercase; letter-spacing: 0.04em; }
/* 名单明细表：深色底、浅色字，铺平不换行；表头与列宽不被压缩 */
.result-table-wrap { width: 100%; overflow-x: auto; }
.result-table-wrap :deep(.el-table) { background: transparent !important; min-width: 1140px !important; }
.result-table-wrap :deep(.el-table th.el-table__cell),
.result-table-wrap :deep(.el-table td.el-table__cell) {
  background: transparent !important;
  border-color: rgba(255,255,255,0.08) !important;
  color: #e2e8f0 !important;
  padding: 8px 6px !important;
  white-space: nowrap;
  font-size: 13px;
  text-align: center;
}
.result-table-wrap :deep(.el-table th .cell),
.result-table-wrap :deep(.el-table td .cell) {
  white-space: nowrap;
}
.result-table-wrap :deep(.el-table th.el-table__cell:first-child .cell),
.result-table-wrap :deep(.el-table td.el-table__cell:first-child .cell) {
  text-align: left;
}
.result-table-wrap :deep(.el-table th.el-table__cell:not(:first-child) .cell),
.result-table-wrap :deep(.el-table td.el-table__cell:not(:first-child) .cell) {
  text-align: center;
}
.result-table-wrap :deep(.el-table th.el-table__cell) {
  background: rgba(255,255,255,0.08) !important;
  color: #94a3b8 !important;
  font-weight: 600;
  white-space: nowrap;
}
.result-table-wrap :deep(.el-table th .cell) {
  white-space: nowrap;
  overflow: visible;
  text-overflow: unset;
}
.result-table-wrap :deep(.el-table tr) { background: transparent !important; }
.result-table-wrap :deep(.el-table tr:hover > td) { background: rgba(255,255,255,0.06) !important; }
.retail-detail-table :deep(.el-table__empty-block) { background: rgba(255,255,255,0.04) !important; }
.retail-detail-table :deep(.el-loading-mask) { background-color: rgba(15,23,42,0.6) !important; }
.retail-detail-table :deep(.el-loading-spinner .path) { stroke: #5eead4; }
.retail-detail-table :deep(.el-loading-spinner .el-loading-text) { color: #e2e8f0; }
.cell-freq-high { color: #5eead4; }
.cell-freq-mid { color: #fcd34d; }
.cell-freq-low { color: #94a3b8; }
.cell-acq-easy { color: #5eead4; }
.cell-acq-mid { color: #fcd34d; }
.cell-acq-hard { color: #94a3b8; }
.cell-scale { color: #e2e8f0; }
.cell-pnl.profit { color: #f87171; }
.cell-pnl.loss { color: #5eead4; }
.cell-pnl { color: #94a3b8; }
.cell-cash.profit { color: #f87171; }
.cell-cash.loss { color: #5eead4; }
.cell-cash { color: #94a3b8; }
.cell-stab { color: #e2e8f0; }
.cell-psych { color: #5eead4; font-weight: 500; }
.group-tags { display: flex; flex-wrap: wrap; gap: 6px; }
.group-tag { display: inline-block; padding: 3px 8px; border-radius: 4px; font-size: 11px; border: 1px solid; font-weight: 500; }
.group-dist-item { align-items: center; }
.group-tag-cell { display: inline-block; padding: 2px 6px; border-radius: 4px; font-size: 11px; border: 1px solid; font-weight: 500; background: transparent; }

@media (max-width: 768px) {
  .dashboard-container .page-header { flex-direction: column; align-items: flex-start; gap: 10px; }
  .dashboard-container .filter-row .el-form { flex-wrap: wrap; }
  .dashboard-container .filter-row .el-select { width: 100% !important; min-width: 0; }
  .dashboard-container .kpi-blocks { grid-template-columns: 1fr; gap: 12px; }
  .dashboard-container .kpi-block { padding: 14px; }
  .dashboard-container .kpi-block-title { font-size: 14px; }
  .dashboard-container .kpi-value { font-size: 20px; }
  .dashboard-container .section.chart-card { padding: 14px; }
  .dashboard-container .core-cards-scroll { overflow-x: auto; -webkit-overflow-scrolling: touch; padding-bottom: 8px; }
  .dashboard-container .shareholder-cards { overflow-x: auto; -webkit-overflow-scrolling: touch; }
  .retail-simulator { padding: 16px; }
  .retail-simulator .simulator-head { flex-direction: column; align-items: flex-start; }
  .retail-simulator .simulator-title { font-size: 16px; }
  .psych-range-row { flex-direction: column; align-items: stretch; gap: 8px; }
  .psych-range-slider-wrap { flex-direction: column; align-items: stretch; }
  .result-summary { flex-wrap: wrap; gap: 12px; }
  .summary-value { font-size: 18px; }
  .result-table-wrap { margin: 0 -8px; }
  .result-table-wrap :deep(.el-table) { min-width: 900px !important; font-size: 12px; }
}
@media (max-width: 480px) {
  .dashboard-container .kpi-value { font-size: 18px; }
  .retail-simulator { padding: 12px; }
}
.assoc-tag {
  display: inline-block;
  font-size: 10px;
  background: #a78bfa;
  color: #fff;
  border-radius: 4px;
  padding: 1px 4px;
  margin-left: 4px;
  vertical-align: middle;
  flex-shrink: 0;
}

.assoc-inline {
  font-size: 12px;
  color: #f97316;
  margin-left: 4px;
  cursor: help;
}
.cell-psych.adj-price {
  color: #c084fc;
  font-weight: 700;
}
.associate-sim-section {
  background: #f0f9eb;
  border: 1px solid #c2e7b0;
  border-radius: 8px;
  padding: 12px 16px;
  margin-bottom: 12px;
}
.associate-sim-header {
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 10px;
  flex-wrap: wrap;
}
.associate-sim-title {
  font-weight: 600;
  font-size: 14px;
  color: #303133;
}
.associate-sim-count {
  font-size: 13px;
  color: #606266;
}
.associate-sim-actions {
  margin-left: auto;
  display: flex;
  gap: 4px;
}
.associate-grid {
  display: flex;
  flex-wrap: wrap;
  gap: 8px 16px;
  margin-bottom: 12px;
  font-size: 13px;
}
.associate-grid :deep(.el-checkbox__label) {
  font-size: 13px;
  color: #303133;
}
.associate-psych-table {
  margin-top: 10px;
  background: #fafafa;
  border-radius: 6px;
  overflow: hidden;
}
.associate-psych-header {
  font-size: 12px;
  color: #606266;
  padding: 6px 10px;
  font-weight: 500;
  border-bottom: 1px solid #e8f5d4;
}
.associate-psych-tbl {
  width: 100%;
  border-collapse: collapse;
  font-size: 12px;
}
.associate-psych-tbl th {
  background: #f0f9eb;
  color: #606266;
  padding: 6px 10px;
  text-align: center;
  font-weight: 500;
}
.associate-psych-tbl td {
  padding: 6px 10px;
  color: #303133;
  text-align: center;
}
.associate-psych-tbl tbody tr:hover {
  background: #f5fbe5;
}
.assoc-psych-val {
  color: #67c23a;
  font-weight: 600;
}
</style>
